import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Icon,
  List,
  ListItem,
  ListItemIcon,
  Menu,
  MenuItem,
  SwipeableDrawer,
  Typography,
  useMediaQuery,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';

const IconMenu = props => {
  const {
    anchorOrigin,
    disableScrollLock,
    icon,
    iconColor,
    items,
    onClose,
    transformOrigin,
    ...otherProps
  } = props;
  const lgUp = useMediaQuery(theme => theme.breakpoints.up('lg'), { noSsr: true });

  const [anchor, setAnchor] = useState(null);

  const isOpen = !!anchor;

  const handleOpen = ({ currentTarget }) => {
    setAnchor(currentTarget);
  };

  const handleClose = () => {
    setAnchor(null);
    onClose();
  };

  const handleClick = item => {
    item.onClick(item.value ?? item.label);
    handleClose();
  };

  return (
    <>
      <Box
        data-testid="ellipsis-menu"
        onClick={handleOpen}
        sx={{
          '&:hover': {
            backgroundColor: 'secondary.light',
          },
          backgroundColor: isOpen ? 'secondary.medium' : 'palette.white',
          borderRadius: '100%',
          color: iconColor ?? 'secondary.main',
          cursor: 'pointer',
          height: 24,
          minWidth: 24,
          paddingBottom: 0,
          width: 24,
        }}
      >
        {icon || <MoreVertIcon />}
      </Box>
      {lgUp ? (
        <Menu
          anchorEl={anchor}
          anchorOrigin={anchorOrigin}
          anchorReference="anchorEl"
          disableScrollLock={disableScrollLock}
          onClose={handleClose}
          open={isOpen}
          transformOrigin={transformOrigin}
          {...otherProps}
        >
          {items.map(item => (
            <MenuItem data-testid={item.testId} key={item.label} onClick={() => handleClick(item)}>
              {item.icon && (
                <ListItemIcon>
                  <Icon>{item.icon}</Icon>
                </ListItemIcon>
              )}
              {item.element || item.label}
            </MenuItem>
          ))}
        </Menu>
      ) : (
        <SwipeableDrawer
          anchor="bottom"
          disableSwipeToOpen
          onClose={handleClose}
          onOpen={handleOpen}
          open={isOpen}
          slotProps={{
            paper: {
              sx: {
                borderTopLeftRadius: 5,
                borderTopRightRadius: 5,
              },
            },
          }}
          swipeAreaWidth={48}
        >
          <Box
            sx={{
              alignSelf: 'center',
              backgroundColor: 'graphic.main',
              borderRadius: '10px',
              height: 2,
              my: 2,
              width: 32,
            }}
          />
          <List>
            {items.map(item => (
              <ListItem key={item.label} onClick={() => handleClick(item)} role="listitem">
                <Typography variant="dropdown">{item.element || item.label}</Typography>
              </ListItem>
            ))}
          </List>
        </SwipeableDrawer>
      )}
    </>
  );
};

IconMenu.propTypes = {
  /**
   * Menu popover origin
   */
  anchorOrigin: PropTypes.shape({}),
  /**
   * Disables scrolling when menu is open
   */
  disableScrollLock: PropTypes.bool,
  /**
   * Icon
   */
  icon: PropTypes.node,
  /**
   * Color of the icon
   */
  iconColor: PropTypes.string,
  /**
   * Items in menu
   */
  items: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  /**
   * Callback fired when menu exits
   */
  onClose: PropTypes.func,
  /**
   * Alignment relative to the anchor element
   */
  transformOrigin: PropTypes.shape({}),
};

IconMenu.defaultProps = {
  anchorOrigin: { horizontal: 'center', vertical: 'bottom' },
  disableScrollLock: true,
  icon: null,
  iconColor: undefined,
  onClose: () => {},
  transformOrigin: { horizontal: 'right', vertical: 'top' },
};

export default IconMenu;
